import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  DialogContentText,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridValueGetter } from '@mui/x-data-grid';
import { MonthlySettlement } from '../../types/payment';
import api from '../../services/api';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useAuth } from '../../contexts/AuthContext';
import { format, lastDayOfMonth } from 'date-fns';

// Add Company type for local use
interface Company {
  id: number;
  name: string;
  brand_name: string;
  is_active: boolean;
  address?: string;
  contact_number?: string;
  email?: string;
}

export default function MonthlySettlements({ onOpenLedger }: { onOpenLedger?: (workerId: string) => void }) {
  const { user } = useAuth();
  const [settlements, setSettlements] = useState<MonthlySettlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [calculateOpen, setCalculateOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState<MonthlySettlement | null>(null);
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState<number | ''>('');
  const [filterYear, setFilterYear] = useState<number | ''>('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'worker_id', headerName: 'Worker ID', width: 120 },
    { 
      field: 'worker', 
      headerName: 'Worker Name', 
      width: 180,
      renderCell: (params: GridRenderCellParams<any, MonthlySettlement>) => {
        const name = (params?.row as any)?.worker?.name;
        const workerId = (params?.row as any)?.worker_id;
        return (
          <Button variant="text" size="small" onClick={(e) => { e.stopPropagation(); if (onOpenLedger && workerId) onOpenLedger(workerId); }}>
            {name || 'N/A'}
          </Button>
        );
      },
      sortable: false,
      filterable: false,
    },
    { 
      field: 'job_type',
      headerName: 'Job Type',
      width: 120,
      renderCell: (params: GridRenderCellParams<any, MonthlySettlement>) => {
        const workerType = (params?.row as any)?.worker?.worker_type;
        return <span>{getJobTypeLabel(workerType) || '-'}</span>;
      },
      sortable: false,
      filterable: false,
    },
    { 
      field: 'month', 
      headerName: 'Month', 
      width: 100,
    },
    { field: 'year', headerName: 'Year', width: 100 },
    { 
      field: 'total_job_amount', 
      headerName: 'Total Job Amount', 
      width: 150,
    },
    { 
      field: 'part_payments_total', 
      headerName: 'Part Payments Total', 
      width: 180,
    },
    {
      field: 'pending_refinish_amount',
      headerName: 'Pending Refinish',
      width: 150,
    },
    { 
      field: 'net_payable', 
      headerName: 'Net Payable', 
      width: 150,
    },
    { field: 'status', headerName: 'Status', width: 120 },
    { 
      field: 'utr_number', 
      headerName: 'UTR Number', 
      width: 150,
      valueGetter: (params: any) => params?.row?.utr_number || 'N/A',
    },
    { 
      field: 'payment_date', 
      headerName: 'Payment Date', 
      width: 150,
      valueGetter: (params: any) =>
        params?.row?.payment_date
          ? new Date(params.row.payment_date).toLocaleDateString()
          : 'N/A',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      renderCell: (params: GridRenderCellParams<any, MonthlySettlement>) => {
        const settlement = params.row;
        return (
          <Box>
            {settlement.status === 'PENDING' && (
              <Tooltip title="Void Settlement">
                <IconButton 
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVoidClick(settlement);
                  }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
            {settlement.status !== 'VOIDED' && (
              <Tooltip title="Print Invoice">
                <IconButton size="small" onClick={() => handlePrintInvoice(settlement)}>
                  <PictureAsPdfIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const fetchData = async () => {
    try {
      const response = await api.get('/payments/monthly-settlements');
      setSettlements(response.data);
    } catch (error) {
      console.error('Error fetching settlements:', error);
      setError('Failed to fetch settlements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCalculate = async () => {
    try {
      await api.post('/payments/calculate-monthly-settlement', { month, year });
      setSuccess('Settlements calculated successfully');
      await fetchData();
      setCalculateOpen(false);
    } catch (error: any) {
      console.error('Error calculating settlements:', error);
      setError(error.response?.data?.error || 'Failed to calculate settlements');
    }
  };

  const handleDownload = async () => {
    try {
      const response = await api.post('/payments/download-settlements', 
        { month, year },
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `settlements-${month}-${year}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      setSuccess('Settlements downloaded successfully');
    } catch (error) {
      console.error('Error downloading settlements:', error);
      setError('Failed to download settlements');
    }
  };

  const handleDownloadPending = async () => {
    try {
      const response = await api.get('/payments/download-pending-settlements', 
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pending_settlements_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      setSuccess('Pending settlements downloaded successfully');
    } catch (error) {
      console.error('Error downloading pending settlements:', error);
      setError('Failed to download pending settlements');
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('settlements_file', file);

      const response = await api.post('/payments/process-settlements', formData);
      
      setSuccess(`Successfully processed ${response.data.processed.length} settlements`);
      await fetchData();

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Error processing settlements file:', error);
      setError(error.response?.data?.error || 'Failed to process settlements file');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleVoidClick = (settlement: MonthlySettlement) => {
    setSelectedSettlement(settlement);
    setVoidOpen(true);
  };

  const handleVoidConfirm = async () => {
    if (!selectedSettlement) return;
    
    try {
      await api.delete(`/payments/monthly-settlements/${selectedSettlement.id}`);
      setSuccess(`Settlement for ${selectedSettlement.worker_id} voided successfully`);
      await fetchData();
      setVoidOpen(false);
    } catch (error: any) {
      console.error('Error voiding settlement:', error);
      setError(error.response?.data?.error || 'Failed to void settlement');
    }
  };

  const handlePrintInvoice = (settlement: MonthlySettlement) => {
    const company = user?.company as Company | undefined;
    const workerName = settlement.worker?.name || settlement.worker_id;
    const workerId = settlement.worker_id;
    // Add worker address and PAN if available
    const workerAddress = (settlement.worker as any)?.address || '';
    const workerPan = (settlement.worker as any)?.pan || '';
    const month = settlement.month;
    const year = settlement.year;
    const fyStart = month >= 4 ? year : year - 1;
    const fyEnd = month >= 4 ? year + 1 : year;
    const invoiceNo = `${month.toString().padStart(2, '0')}/${fyStart}-${fyEnd}/01`;
    const invoiceDate = format(lastDayOfMonth(new Date(year, month - 1)), 'dd/MM/yyyy');
    const doc = new jsPDF();
    // Header: Worker details
    doc.setFontSize(18);
    doc.text(workerName, 105, 18, { align: 'center' });
    doc.setFontSize(11);
    let headerY = 26;
    if (workerAddress) {
      doc.text(workerAddress, 105, headerY, { align: 'center' });
      headerY += 6;
    }
    if (workerPan) {
      doc.text(`PAN: ${workerPan}`, 105, headerY, { align: 'center' });
      headerY += 6;
    }
    doc.setFontSize(16);
    doc.text('INVOICE', 105, headerY + 4, { align: 'center' });
    // Invoice details
    doc.setFontSize(11);
    doc.text(`Invoice No: ${invoiceNo}`, 20, headerY + 14);
    doc.text(`Invoice Date: ${invoiceDate}`, 150, headerY + 14);
    // Bill to: Company details
    doc.text('Bill To:', 20, headerY + 24);
    let billToY = headerY + 30;
    if (company) {
      doc.text(company.brand_name || company.name || 'Company Name', 20, billToY);
      billToY += 6;
      if (company.address) {
        doc.text(company.address, 20, billToY);
        billToY += 6;
      }
      if (company.contact_number) {
        doc.text(`Contact: ${company.contact_number}`, 20, billToY);
        billToY += 6;
      }
      if ((company as any).gst) {
        doc.text(`GST: ${(company as any).gst}`, 20, billToY);
        billToY += 6;
      }
    } else {
      doc.text('Company Name', 20, billToY);
      billToY += 6;
    }
    // Job work details grid
    (doc as any).autoTable({
      startY: billToY + 5,
      head: [[
        'Description',
        'Job Type',
        'Month',
        'Year',
        'Total Job Amount',
        'Part Payments',
        'Pending Refinish',
        'Net Payable',
      ]],
      body: [[
        'Job Work Charges',
        getJobTypeLabel(settlement.worker?.worker_type),
        settlement.month,
        settlement.year,
        settlement.total_job_amount.toFixed(2),
        settlement.part_payments_total.toFixed(2),
        settlement.pending_refinish_amount.toFixed(2),
        settlement.net_payable.toFixed(2),
      ]],
      theme: 'grid',
      headStyles: { fillColor: [52, 152, 219], textColor: 255 },
      bodyStyles: { textColor: 50 },
      styles: { fontSize: 11 },
    });
    const amountY = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(11);
    doc.text(`Amount Payable (in words): Rs. ${numberToWords(settlement.net_payable)} only`, 20, amountY);
    // Add system generated note at the bottom
    doc.setFontSize(9);
    doc.text('This is a system generated invoice and does not require signature.', 105, 285, { align: 'center' });
    doc.save(`Invoice_${workerName}_${invoiceNo}.pdf`);
  };

  function numberToWords(num: number): string {
    const a = [ '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen' ];
    const b = [ '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety' ];
    if (num === 0) return 'Zero';
    if (num < 20) return a[num];
    if (num < 100) return b[Math.floor(num / 10)] + (num % 10 ? ' ' + a[num % 10] : '');
    if (num < 1000) return a[Math.floor(num / 100)] + ' Hundred' + (num % 100 ? ' and ' + numberToWords(num % 100) : '');
    if (num < 100000) return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 ? ' ' + numberToWords(num % 1000) : '');
    if (num < 10000000) return numberToWords(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 ? ' ' + numberToWords(num % 100000) : '');
    return num.toString();
  }

  // Helper to get user-friendly job type label
  function getJobTypeLabel(type?: string) {
    if (!type) return '-';
    switch (type) {
      case 'STITCHING': return 'Stitching';
      case 'PRESS': return 'Press';
      default: return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    }
  }

  // Filter settlements based on selected month and year
  const filteredSettlements = settlements.filter((settlement) => {
    if (filterMonth !== '' && settlement.month !== filterMonth) {
      return false;
    }
    if (filterYear !== '' && settlement.year !== filterYear) {
      return false;
    }
    return true;
  });

  // Generate year options (last 5 years to current year + next year)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 7 }, (_, i) => currentYear - 5 + i);

  // Month names for dropdown
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Monthly Settlements</Typography>
        <Stack direction="row" spacing={2}>
          <Box>
            <Tooltip title="Download Pending Settlements">
              <IconButton onClick={handleDownloadPending} sx={{ mr: 1 }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Upload Processed Settlements">
              <IconButton 
                onClick={handleUploadClick} 
                sx={{ mr: 1 }}
                disabled={uploadLoading}
              >
                {uploadLoading ? <CircularProgress size={24} /> : <UploadIcon />}
              </IconButton>
            </Tooltip>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => setCalculateOpen(true)}
            >
              Calculate Settlements
            </Button>
          </Box>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Filter by Month</InputLabel>
          <Select
            value={filterMonth}
            label="Filter by Month"
            onChange={(e) => setFilterMonth(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <MenuItem value="">All Months</MenuItem>
            {monthNames.map((name, index) => (
              <MenuItem key={index + 1} value={index + 1}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Filter by Year</InputLabel>
          <Select
            value={filterYear}
            label="Filter by Year"
            onChange={(e) => setFilterYear(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <MenuItem value="">All Years</MenuItem>
            {yearOptions.map((yr) => (
              <MenuItem key={yr} value={yr}>
                {yr}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {(filterMonth !== '' || filterYear !== '') && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setFilterMonth('');
              setFilterYear('');
            }}
          >
            Clear Filters
          </Button>
        )}
      </Box>

      <DataGrid
        rows={filteredSettlements}
        columns={columns}
        loading={loading}
        pageSizeOptions={[10, 20, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 20 } },
          sorting: {
            sortModel: [
              { field: 'year', sort: 'desc' },
              { field: 'month', sort: 'desc' },
            ],
          },
        }}
        sx={{
          height: 1148, // 20 rows * 52px row height (1040px) + header (56px) + pagination (52px)
          '@media (max-width: 768px)': {
            height: 'auto',
            minHeight: 400,
          },
        }}
      />

      <Dialog 
        open={calculateOpen} 
        onClose={() => setCalculateOpen(false)}
      >
        <DialogTitle>Calculate Monthly Settlements</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Calculate settlements for the selected month and year.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Month"
              type="number"
              inputProps={{ min: 1, max: 12 }}
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              fullWidth
            />
            <TextField
              label="Year"
              type="number"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalculateOpen(false)}>Cancel</Button>
          <Button onClick={handleCalculate} variant="contained" color="primary">
            Calculate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={voidOpen} 
        onClose={() => setVoidOpen(false)}
      >
        <DialogTitle>Void Settlement</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to void the settlement for worker
            <strong>{selectedSettlement?.worker?.name || selectedSettlement?.worker_id}</strong>
            for {selectedSettlement?.month}/{selectedSettlement?.year}?
            <br /><br />
            This will unlock any worker assignments included in this settlement and remove the settlement record.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidOpen(false)}>Cancel</Button>
          <Button onClick={handleVoidConfirm} variant="contained" color="error">
            Void Settlement
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 